import { FEED_LIMIT } from '../../shared/queries.ts';
import { ago, issueKey } from '../format.ts';
import { useLive } from '../live.ts';
import { nameOf } from '../types.ts';
import type { FeedItem } from '../types.ts';
import { StatusMark } from './Marks.tsx';

/**
 * The newest comments across every issue — a different table at the root, and the same joins.
 *
 * The one view that is deliberately CROSS-project. It stays unscoped because it does not need a
 * partition to be bounded: it is a seek into `comment_by_created` with a limit, so its cost is the
 * limit whatever the store holds. It therefore shows each comment's project, since a feed row whose
 * issue is in a project you are not looking at is the common case here and not the exception.
 */
export function Activity({ onOpen }: { onOpen: (id: string) => void }) {
  const live = useLive<FeedItem[]>('feed', { limit: FEED_LIMIT });
  const rows = live.data ?? [];
  return (
    <section className="pane feed-pane" aria-label="Activity">
      <header className="pane-head">
        <span className="eyebrow">ACTIVITY / NEWEST COMMENTS</span>
        <span className="micro muted">{live.status === 'live' ? `LAST ${rows.length}` : live.status.toUpperCase()}</span>
      </header>
      <ol className="feed">
        {rows.map(item => {
          const issue = item.issue?.[0];
          return (
            <li key={item.id} className="feed-item">
              <div className="feed-line">
                <strong>{nameOf(item.author, item.authorId)}</strong>
                <span className="muted"> commented on </span>
                {issue ? (
                  <button type="button" className="link" onClick={() => onOpen(issue.id)}>
                    <StatusMark status={issue.status} />
                    {issue.project?.[0] && <span className="project-key">{issue.project[0].key}</span>}
                    {' '}<span className="issue-key">{issueKey(issue.id)}</span> {issue.title}
                  </button>
                ) : <span className="muted">a deleted issue</span>}
                <span className="micro muted feed-time">{ago(item.createdAt)}</span>
              </div>
              <p className="feed-body">{item.body}</p>
            </li>
          );
        })}
      </ol>
      {!rows.length && <p className="empty">{live.status === 'live' ? 'No comments yet.' : 'Opening a live query…'}</p>}
    </section>
  );
}
